/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {SelectInTableColorModel} from './models/select-in-table-color.model';
import {isFocusedClick} from '../utils/click-inside-element';

import {NgbDate, NgbDateStruct, NgbDropdown} from '@ng-bootstrap/ng-bootstrap';
import {PrettyPrintReferencePipe} from '../pipes/pretty-print-reference/pretty-print-reference.pipe';
import {DropdownType} from './models/dropdown-type.enum';
import {DropdownOptionModel} from './models/dropdown-option.model';
import {AppService} from '../../app.service';
import {Subscription, timer} from 'rxjs';
import {DisplayObjectModel} from './models/display-object.model';
import {DateRangeModel} from './models/date-range.model';
import * as moment from 'moment';
import {Moment} from 'moment';
import {InputDateComponent} from '../input/input-date/input-date.component';
import {DatepickerHelperService} from '../utils/datepicker-helper/datepicker-helper.service';
import {ToastWrapperService} from '../utils/toast-wrapper-service/toast-wrapper-service';
import {ReferenceDataModel} from '../../api/reference-data.model';

/**
 * @deprecated This component will be deleted in the future.
 */
@Component({
    selector: 'app-select-in-table',
    templateUrl: './select-in-table.component.html',
    styleUrls: ['./select-in-table.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SelectInTableComponent implements OnInit, OnDestroy, AfterViewChecked {

    /** Variable to get a success flag from the parent **/
    @Input() success = new EventEmitter<boolean>();
    /** Variable to pass the placeholder text **/
    @Input() placeholderLabel = 'Select';
    /** Variable to pass the dropdown options**/
    @Input() dropdownOptions: DropdownOptionModel[] = [];
    @Input() dropdownTooltip: TemplateRef<any>;
    /** Variable to store a pre-existing value for the component (display) **/
    @Input() displayObject: DisplayObjectModel;
    /** Variable to add CSS color to the text and the border **/
    @Input() styleObject: SelectInTableColorModel = {text: '#000000', border: '#000000'};
    /** Variable to enable or disable the close (delete) button**/
    @Input() enableDelete: boolean;
    /** Variable to determine the autoclose for the dropdown **/
    @Input() autoClose = false;
    /** Variable to determine the type of dropdown **/
    @Input() dropdownType: DropdownType = DropdownType.DESCRIPTION;
    /** Variable to emit the selected value from the dropdown **/
    @Output() selectedDropdownOption = new EventEmitter<DropdownOptionModel>();
    /** Variable to emit the close button click **/
    @Output() closeClickEvent = new EventEmitter<void>();
    /** Variable to show / hide the input boxes **/
    @Input() showInputBoxes = true;
    /** Variable to show / hide date input **/
    @Input() showDateInput: boolean;
    @Input() useCodeOrName: 'code' | 'name' = 'code';

    @ViewChild('closeButton') closeButton: ElementRef;
    @ViewChild('dateInput') dateInput: InputDateComponent;
    @ViewChild('dropdown') dropdown: NgbDropdown;

    /** Variable to determine if the text in the dropdown toggle should be showed as placeholder **/
    public isPlaceholder = true;
    /** Variable to show the expired style component **/
    public showExpired: boolean;
    /** Variable to close the component**/
    public closeComponent = false;
    /** Enum of the dropdown available types **/
    public dropdownTypeEnum = DropdownType;
    /** Variable to hold the start date of the selected option **/
    public startDate: NgbDate;
    /** Variable to store a or new selected option **/
    public selectedOption: DropdownOptionModel = {code: null, name: null, active: true};

    public willExpire = false;
    /** Variables to manage clicks in the component **/
    private clicksInComponent = 0;
    private clickWasInside: boolean;
    private clickWasInDatepicker: boolean;
    /** Subscriptions variable **/
    private subscriptions = new Subscription();

    get selectedOptionHasTooltip(): boolean {
        return this.selectedOption?.tooltip != null;
    }

    constructor(private elementRef: ElementRef,
                private prettyReferencePipe: PrettyPrintReferencePipe,
                private appService: AppService,
                private datepickerHelperService: DatepickerHelperService,
                private changeDetector: ChangeDetectorRef,
                private toast: ToastWrapperService) {
        this.subscriptions.add(this.appService.onClick.subscribe(this.documentClick));
    }

    /**
     * Toggles the expiration warning according to the selected date
     * @return void
     */
    showExpirationWarning(): boolean {
        const daysUntilExpiration = moment.utc(this.selectedOption.date)
                .endOf('day')
                .diff(moment.utc()
                        .startOf('day'), 'd');
        return daysUntilExpiration <= this.selectedOption.expirationWarningDays;
    }

    ngOnInit(): void {
        this.setInitialComponentDisplay(this.displayObject);

        //  Creating dynamically a new view (ex.: datepicker) from a lifecycle hook (ex.: ngOnInit) will result in
        //  an 'ExpressionChangedAfterItHasBeenCheckedError'. Using timer() is an workaround.
        timer(0)
                .subscribe(() => {
                    this.enableDateInput(false);
                });
    }

    ngAfterViewChecked(): void {
        this.changeDetector.detectChanges();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /** Sets the initial component display
     * @param displayObject The display object details
     */
    setInitialComponentDisplay = (displayObject: DisplayObjectModel): void => {
        if (displayObject.string) {
            this.showInputBoxes = false;
            this.placeholderLabel = displayObject.string;
            this.showDateInput = false;
            /** Set selected option values **/
            this.selectedOption.maxNumberOfDays = displayObject.maxNumberOfDays;
            this.selectedOption.code = displayObject.string;
            this.selectedOption.date = displayObject.expirationDate;
            this.selectedOption.expirationWarningDays = displayObject.expirationWarningDays;
            this.selectedOption.expirationWarning = displayObject.expirationWarning;
            this.manageExpiredStatus();
        }
        this.expirationWarning();
    };

    /**
     * Selects one option from the description type dropdown and closes it
     * @param option The selected option that is passed through the emitted event
     * @return void
     * */
    setDescriptionSelectedOption = (option: DropdownOptionModel): void => {
        this.showDateInput = !!option.maxNumberOfDays;
        this.placeholderLabel = option.code;
        this.selectedOption = {...option};
        /** Set initial date **/
        if (option.maxNumberOfDays) {
            this.setStartDate(option.maxNumberOfDays);
            this.selectedOption.date = this.datepickerHelperService.convertNgbDateToUtcTimestamp(this.startDate);
            /** Set datepicker range **/
            this.setDateRange();
        } else {
            this.selectedOption.date = null;
        }
        this.enableDateInput(true);
        /** Display variables **/
        this.isPlaceholder = false;

        this.dropdown.close();
    };

    setDatepickerValue = (date: NgbDate): void => {
        this.selectedOption.date = this.datepickerHelperService.convertNgbDateToUtcTimestamp(date);
        this.startDate = date;
        this.expirationWarning();
    };

    /**
     * Selects one option from the tree type dropdown and closes it
     * @param option The selected option that is passed through the emitted event
     * */
    setTreeSelectedOption = (option: DropdownOptionModel): void => {
        const optionReferenceData: ReferenceDataModel = {
            name: option?.name,
            code: option?.code,
            active: option?.active
        };
        this.placeholderLabel = this.prettyReferencePipe.transform(optionReferenceData);
        this.selectedOption.code = option.code;
        this.selectedOption.extra = option.extra;
        this.selectedOption.tooltip = this.prettyReferencePipe.transform(optionReferenceData);
        this.isPlaceholder = false;
        this.dropdown.close();
    };

    /**
     * Removes the component from the DOM and emits close event
     * @return void
     * */
    closeClick = (): void => {
        this.closeClickEvent.emit();
    };

    /**
     * Sets the date range (in {@link NgbDateStruct}) to be showed in the datepicker
     * @return DateRangeModel of the date range to be set
     * **/
    setDateRange = (): DateRangeModel => {
        const maxDate = this.getMaxDate(this.selectedOption.maxNumberOfDays)
                .valueOf();
        const minDate = moment.utc()
                .valueOf();
        return {
            minDate: this.datepickerHelperService.convertUtcTimestampToNgbDate(minDate),
            maxDate: this.datepickerHelperService.convertUtcTimestampToNgbDate(maxDate)
        };
    };

    /**
     * Sets and updates the date to be display in the datepicker when a new option is selected
     * @param maxNumberOfDays The max number of days of the selected options
     * @return void
     * **/
    setStartDate = (maxNumberOfDays: number): void => {
        let dateToSet: NgbDate;

        if (!maxNumberOfDays) {
            dateToSet = null;
        } else {
            const maxDate = this.getMaxDate(maxNumberOfDays)
                    .valueOf();
            dateToSet = this.datepickerHelperService.convertUtcTimestampToNgbDate(maxDate);
        }
        this.startDate = dateToSet;
        timer(0)
                .subscribe(() => {
                    this.dateInput.setValue(dateToSet);
                });
    };

    /**
     * Listens to all clicks in the DOM
     * Ignores the 1st click so the component can load with its initial properties
     * @return void
     * */
    documentClick = (event: Event): void => {
        /** In edition mode **/
        if (this.clicksInComponent > 0 && this.showInputBoxes && !this.clickWasInside) {
            this.manageClicksInEditionMode(event);
            /** Date edition in display mode **/
        } else {
            this.manageClicksInDisplayMode(event);
        }
        this.clickWasInDatepicker = false;
        this.clickWasInside = false;
        this.clicksInComponent++;
    };

    /**
     * Manages the component clicks while in Edit Mode
     * @param event The click event
     * @return void
     * **/
    manageClicksInEditionMode = (event: Event): void => {
        this.clickWasInside = isFocusedClick((event.target as Element), this.elementRef);
        this.showInputBoxes = this.clickWasInside;
        if (!this.clickWasInside) {
            if (!this.selectedOption.code) {
                this.closeComponent = true;
                this.closeClickEvent.emit();
            } else {
                this.selectedDropdownOption.emit(this.selectedOption);
                this.showDateInput = false;
            }
        }
    };

    /**
     * Manages the component clicks while in Display Mode
     * @param event The click event
     * @return void
     */
    manageClicksInDisplayMode = (event: Event): void => {
        this.clickWasInside = isFocusedClick((event.target as Element), this.elementRef);
        const wasCloseClick = isFocusedClick((event.target as Element), this.closeButton);
        if (!this.clickWasInside && this.clicksInComponent > 0 && this.showDateInput) {
            if (!this.clickWasInDatepicker) {
                if (this.selectedDateHasValidValue()) {
                    // If the selected date has a valid value, send backend request
                    this.emitUpdatedSelectedOption();
                } else {
                    // If the selected date is invalid, show warning toast and reset the display component to the initial value
                    this.showWarningAndResetSelectedOptionToInitialValue();
                }
                this.showDateInput = false;
            }
        } else if (this.clickWasInside && !wasCloseClick && !this.showDateInput) {
            /** Datepicker initial setup **/
            this.showDateInput = this.clickWasInside && (this.displayObject.expirationWarningDays != null);
            this.datepickerInitialSetup(this.selectedOption);
        }
    };

    /**
     * Datepicker initial setup
     * @param selectedOption The selected option that sets the datepicker initial values
     */
    datepickerInitialSetup = (selectedOption: DropdownOptionModel): void => {
        const currentDate: NgbDate = this.datepickerHelperService.convertUtcTimestampToNgbDate(this.selectedOption.date);
        if (selectedOption.maxNumberOfDays) {
            timer(0)
                    .subscribe(() => {
                        if (this.showExpired) {
                            const maxDate = this.getMaxDate(selectedOption.maxNumberOfDays)
                                    .valueOf();
                            this.dateInput.setValue(maxDate);
                        } else {
                            this.dateInput.setValue(currentDate);
                        }
                    });
        }
    };

    /**
     * Sets the variable {@link clickWasInside} as true when the click occurs inside the datepicker
     * @return void
     */
    preventCloseOnDatepickerClick = (): void => {
        this.clickWasInside = true;
        this.clickWasInDatepicker = true;
    };

    /**
     * Determines if the expiration warning should be showed by setting {@link willExpire} value
     */
    expirationWarning = (): void => {
        this.willExpire = this.showExpirationWarning() && !this.showExpired;
    };

    /**
     * Determines if the value inserted in the datepicker is valid, i.e. input is valid, value is not null and is a number
     */
    private selectedDateHasValidValue = (): boolean => this.dateInput != null && this.dateInput.isValid
            && this.selectedOption.date != null && !isNaN(this.selectedOption.date);

    /**
     * Emit new selected option value
     */
    private emitUpdatedSelectedOption(): void {
        this.selectedDropdownOption.emit(this.selectedOption);
        // If new date is set successfully, set it to the displayObject
        this.subscriptions.add(this.success.subscribe((wasSuccessful: boolean) => {
            if (wasSuccessful) {
                this.displayObject.expirationDate = this.selectedOption.date;
                this.showExpired = false;
            }
        }));
    }

    /**
     * Show warning toast and reset the selected option to the initial value
     */
    private showWarningAndResetSelectedOptionToInitialValue(): void {
        this.toast.warning(`The expiration date of the role was not changed due to a wrong or undefined input.`
                + `<p></br><strong>Please, define a right input or pick a date.</strong></p>`, 'Warning');
        this.setInitialComponentDisplay(this.displayObject);
    }

    /**
     * Toggles the expiration status according to the role expiration date
     * @return void
     * **/
    private manageExpiredStatus = (): void => {
        const today = moment.utc()
                .valueOf();
        const expirationDate = moment.utc(this.selectedOption.date)
                .endOf('day')
                .valueOf();
        this.showExpired = today > expirationDate;
    };

    /**
     * Gives the max date for the option date range
     * @param maxNumberOfDays The selected option maxNumberOfDays
     * @return maxDate Moment object
     * **/
    private getMaxDate = (maxNumberOfDays: number): Moment => moment.utc()
            .add(maxNumberOfDays - 1, 'd');

    private enableDateInput = (enable: boolean): void => {
        if (this.dateInput) {
            if (enable) {
                this.dateInput.enable();
            } else {
                this.dateInput.disable();
            }
        }
    };
}
